import { Box, Divider, Typography } from "@mui/material";
import React from "react";
import { fetchData } from "../../../common/services/ApiClient";
import { fileNameFormat, reduceObj } from "../../../common/services/Utilities";
import { ApiBaseUrl } from "../../../config";
import ResultPagination from "../../../containers/ResultPagination";
import SearchPills from "../../../containers/SearchPills";
import {
  InsuredDataValues,
  KeyValue,
  Metadata,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../interfaces";
import {
  primaryHeaderColor,
  textHeaderColor,
} from "../../../refdata/constants";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import MultiHeaderSummaryTable from "../../MultiHeaderTable/MultiHeaderSummaryTable";
import { transformDataForDownload } from "./SummaryTableUtilities";
import ShowHideButton from "../../ShowHideButton";

interface Props {
  appState: Query;
}

export default function SummaryTablesByMSA({ appState }: Props) {
  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );

  const [downloadData, setDownloadData] = React.useState<KeyValue[]>();
  const [loading, setLoading] = React.useState(false);
  const [msaDataList, setMsaDataList] = React.useState<{
    [key: string]: InsuredDataValues;
  }>({});
  const [previousYear, setPreviousYear] = React.useState("");
  const [totalResults, setTotalResults] = React.useState<KeyValue>({});
  const [metaData, setMetaData] = React.useState<Metadata>({
    count: 0,
  });
  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat("Summary_Tables_By_MSA_formatted")}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      footnotes: [
        appState.filters.lastYear === "1994"
          ? "*Includes 18 conservatorships"
          : null,
        "  *Total reflects the sum of all MSAs.",
      ].filter(Boolean) as string[],
    },
  ];

  const headingTitle = (
    <Box
      sx={{
        marginTop: -1,
        display: "flex",
        alignItems: "end",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        Regional Totals by Metropolitan Statistical Area (MSA)
      </Typography>
      <ApiDataDownload
        app="SOD"
        apiData={apiData}
        apiPathLink="sod_msa"
        downloadData={downloadData}
      />
    </Box>
  );

  const rowsPerPage = appState.pagination.resultLimit
    ? appState.pagination.resultLimit
    : 25;

  const page = appState.pagination.pageNumber
    ? appState.pagination.pageNumber
    : 1;

  const processData = React.useCallback(
    (response: KeyValue[], firstCol: string[]) => {
      let tableData = reduceObj(
        firstCol.map((state: string) => {
          return {
            key: state,
            value: {
              all: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
              CB: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
              SA: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
              IB: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
            },
          };
        }),
      );
      const dataMap = ["all", "CB", "SA", "IB"];
      dataMap.forEach((key: string, i) => {
        for (const data of response[i].data) {
          let msa: string = data.MSANAMB;
          if (tableData.hasOwnProperty(msa)) {
            tableData[msa][key] = {
              unique_count: data.unique_count,
              sum_DEPSUMBR: data.sum_DEPSUMBR,
              count: data.count,
            };
          }
        }
      });

      setMsaDataList(tableData);
      let resultTotals: KeyValue = {};
      for (const data of response[4].data) {
        resultTotals[data["INSURED"]] = {
          unique_count: data.sum_BKMO,
          sum_DEPSUMBR: data.sum_DEPSUMBR,
          count: data.count,
        };
      }
      const all = {
        all: {
          unique_count: response[4].totals.sum_BKMO,
          sum_DEPSUMBR: response[4].totals.sum_DEPSUMBR,
          count: response[4].totals.count,
        },
      };
      resultTotals = { ...resultTotals, ...all };
      const totals = {
        "MSA Total": resultTotals,
      };
      const tableDataWithTotals = { ...tableData, ...totals };
      const resultForDownload = transformDataForDownload(
        tableDataWithTotals,
        `06/30/${appState.filters.lastYear}`,
        "MSA",
      );
      setDownloadData(resultForDownload);
      setTotalResults(resultTotals);
    },
    [appState.filters.lastYear],
  );

  React.useEffect(() => {
    let isMounted = true;
    if (isMounted && appState.filters.lastYear !== previousYear) {
      setLoading(true);
      setPreviousYear(appState.filters.lastYear);
      (async () => {
        await Promise.all([
          //all institutions
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND !MSANAMB:""&agg_by=MSANAMB&agg_sum_fields=DEPSUMBR&agg_limit=10000&get_unique=CERT`,
          ),
          // commercial banks
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:CB AND !MSANAMB:""&agg_by=MSANAMB&agg_sum_fields=DEPSUMBR&agg_limit=10000&get_unique=CERT`,
          ),
          //savings banks
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA AND !MSANAMB:""&agg_by=MSANAMB&agg_sum_fields=DEPSUMBR&agg_limit=10000&get_unique=CERT`,
          ),
          //foreign banks
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:IB AND !MSANAMB:""&agg_by=MSANAMB&agg_sum_fields=DEPSUMBR&agg_limit=10000&get_unique=CERT`,
          ),
          //totals of all bank types
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=INSURED&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
              appState.filters.lastYear
            } AND !MSANAMB:""`,
          ),
          //msa count for pagination
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND !MSANAMB:""&get_unique=MSANAMB&limit=1&fields=MSANAMB`,
          ),
        ])
          .then((response) => {
            const allResponse = response[0].data;
            const allMSANames = allResponse.map(
              (data: KeyValue) => data.MSANAMB,
            );

            setMetaData(response[5].totals);
            processData(response, allMSANames);
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [appState.filters.lastYear, previousYear, processData]);

  const msaHeader: TableHeader[] = [
    {
      title: "MSA",
      styles: {
        backgroundColor: primaryHeaderColor,
        color: textHeaderColor,
        textAlign: "left",
      },
      colSpan: 3,
      apiField: "",
    },
  ];

  const paginatedDataList =
    Object.keys(msaDataList).length > 0
      ? Object.fromEntries(
          Object.entries(msaDataList).slice(
            (page - 1) * rowsPerPage,
            rowsPerPage * page,
          ),
        )
      : {};

  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}
      <ResultPagination count={metaData.count}>
        <MultiHeaderSummaryTable
          tableData={paginatedDataList}
          totalsData={totalResults}
          firstHeader={msaHeader}
          showAsterisks={false}
          loading={loading}
          tableType={"msa"}
          lastYear={appState.filters.lastYear}
        />
      </ResultPagination>
      {appState.filters.lastYear === "1994" && (
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.main,
            width: 900,
            minWidth: 900,
            fontSize: 16,
            fontWeight: "bold",
          }}
        >
          *Includes 18 conservatorships
        </Typography>
      )}
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          width: 900,
          minWidth: 900,
          fontSize: 16,
          fontWeight: "bold",
          fontStyle: "italic",
        }}
      >
        *Total reflects the sum of all MSAs.
      </Typography>
    </>
  );
}
